import { formatDate as localeDate } from 'i18n/format';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
  goToConsent,
  oauthErrorText,
  useOAuthGrants,
  useOAuthProviders,
  useRevokeGrant,
  useStartOAuth,
} from 'api/oauth';
import { extractApiError } from 'api/errors';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import useAuthStore from 'stores/authStore';
import useRole from 'hooks/useRole';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

const RETURN_TO = '/console/connectors/accounts';

const STATUS = {
  get active() { return { label: t('data.active'), color: 'success' }; },
  get broken() { return { label: t('data.reconnectNeeded'), color: 'warning' }; },
  get revoked() { return { label: t('data.disconnected'), color: 'default' }; },
};

function formatDate(iso) {
  if (!iso) return '—';
  return localeDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });
}

/** Every external account the tenant signed in with: reconnect or disconnect it. */
export default function ConnectedAccountsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useRole();
  const user = useAuthStore((s) => s.user);
  const { data: grants, isLoading, error } = useOAuthGrants();
  const { data: providers } = useOAuthProviders();
  const revoke = useRevokeGrant();
  const start = useStartOAuth();

  const [toRevoke, setToRevoke] = useState(null);
  const [revokeError, setRevokeError] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [startError, setStartError] = useState(null);

  const connectedId = searchParams.get('oauth_grant');
  const errorCode = searchParams.get('oauth_error');
  const enabledProviders = (providers || []).filter((p) => p.enabled);

  const connect = async (providerKey) => {
    setMenuAnchor(null);
    setStartError(null);
    try {
      const data = await start.mutateAsync({ provider: providerKey, returnTo: RETURN_TO });
      goToConsent(data.authorize_url);
    } catch (err) {
      setStartError(extractApiError(err, t('data.couldNotStartTheSignInPleaseTryAgain')));
    }
  };

  const confirmRevoke = async () => {
    if (!toRevoke) return;
    setRevokeError(null);
    try {
      await revoke.mutateAsync(toRevoke.public_id);
      setToRevoke(null);
    } catch (err) {
      setRevokeError(extractApiError(err, t('data.failedToDisconnectTheAccountPleaseTryAgain')));
    }
  };

  const mayDisconnect = (grant) =>
    isAdmin || (user?.email && grant.created_by_email === user.email);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/console/connectors')}
            size="small"
            color="inherit"
          >{t('data.back')}</Button>
          <Divider orientation="vertical" flexItem />
          <AccountCircleIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('data.connectedAccounts')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('data.externalAccountsThisWorkspaceHasSignedInWithTokensStayOn')}</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddLinkIcon />}
          disabled={enabledProviders.length === 0 || start.isPending}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >{t('data.connectAccount')}</Button>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          {enabledProviders.map((p) => (
            <MenuItem key={p.key} onClick={() => connect(p.key)}>
              {p.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {connectedId && grants?.some((g) => g.public_id === connectedId) && (
        <Alert severity="success" sx={{ mb: 2 }}>{t('data.accountConnectedUseItWhenCreatingAConnection')}</Alert>
      )}
      {errorCode && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {oauthErrorText(errorCode)}
        </Alert>
      )}
      {startError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {startError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('data.failedToLoadConnectedAccounts')}</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : grants?.length === 0 ? (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 6, textAlign: 'center' }}>
          <Typography fontWeight={700}>{t('data.noConnectedAccountsYet')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('data.connectOneHereOrFromAConnectionFormByChoosingA')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>{t('data.provider')}</TableCell>
                <TableCell>{t('data.account')}</TableCell>
                <TableCell>{t('data.access')}</TableCell>
                <TableCell>{t('data.status')}</TableCell>
                <TableCell>{t('data.usedBy')}</TableCell>
                <TableCell>{t('data.connectedBy')}</TableCell>
                <TableCell>{t('data.connected')}</TableCell>
                <TableCell align="right">{t('data.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grants?.map((grant) => {
                const status = STATUS[grant.status] || { label: grant.status, color: 'default' };
                const revoked = grant.status === 'revoked';
                return (
                  <TableRow key={grant.public_id} sx={{ opacity: revoked ? 0.55 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {grant.provider_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {grant.external_login || grant.external_account_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(grant.scopes || []).map((s) => (
                          <Chip key={s} label={s} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          grant.status === 'broken'
                            ? t('data.theProviderNoLongerAcceptsTheSavedAccessReconnectToResume')
                            : ''
                        }
                      >
                        <Chip label={status.label} color={status.color} size="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {grant.connections.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {grant.connections.map((c) => (
                            <Chip
                              key={c.public_id}
                              label={c.name}
                              size="small"
                              onClick={() => navigate(`/console/connectors/${c.public_id}`)}
                              color={c.status === 'error' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {grant.created_by_email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(grant.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {grant.status === 'broken' && (
                        <Tooltip title={t('data.reconnect')}>
                          <IconButton size="small" onClick={() => connect(grant.provider)} disabled={start.isPending}>
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!revoked && mayDisconnect(grant) && (
                        <Tooltip title={t('data.disconnect')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setRevokeError(null); setToRevoke(grant); }}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmActionDialog
        open={!!toRevoke}
        title={t('data.disconnectAccount')}
        confirmWord={t('data.actiondisconnect')}
        confirmLabel={t('data.disconnect')}
        confirmColor="error"
        loading={revoke.isPending}
        error={revokeError}
        onConfirm={confirmRevoke}
        onClose={() => setToRevoke(null)}
      >
        <Typography variant="body2">{t('data.thePlatformWillGiveUpItsAccessTo')}{' '}
          <strong>{toRevoke?.external_login || toRevoke?.external_account_id}</strong>{' '}{t('data.at')}{' '}
          {toRevoke?.provider_name}.
          {toRevoke?.connections?.length ? (
            <>
              {' '}
              {toRevoke.connections.length === 1
                ? t('data.theConnectionThatUsesItGoes')
                : t('data.theValueConnectionsThatUseItGo', { value1: toRevoke.connections.length })}{' '}{t('data.offlineAndTheirPipelinesPauseUntilAnAccountIsConnectedAgain')}</>
          ) : null}
        </Typography>
      </ConfirmActionDialog>
    </Box>
  );
}
